import { CSSProperties } from "react";
import { useInView } from "react-intersection-observer";

/** Shared easing for every entrance — a soft, decelerating "ease-out-expo". */
export const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

/**
 * Evaluated once at module load. When the visitor asks the OS to reduce motion
 * we skip every entrance and leave content in its final, static state.
 */
export const REDUCED =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

interface RevealOptions {
  /** Stagger, in ms, before this element starts animating. */
  delay?: number;
  /** "up" fades in while sliding up; "scale" fades in while easing up from 94%. */
  variant?: "up" | "scale";
  /** Travel distance for the "up" variant. */
  distance?: number;
  /** Fraction of the element that must be visible to trigger. */
  threshold?: number;
}

/**
 * Scroll-triggered reveal. Returns a `ref` to attach to the element and the
 * inline `style` that drives its entrance. Fires once, a touch before the
 * element is fully in view. The final state clears `transform` entirely so it
 * never shadows a `:hover` transform defined in CSS.
 */
export function useReveal({
  delay = 0,
  variant = "up",
  distance = 28,
  threshold = 0.15,
}: RevealOptions = {}) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold,
    rootMargin: "0px 0px -8% 0px",
  });

  const visible = REDUCED || inView;

  const style: CSSProperties | undefined = REDUCED
    ? undefined
    : {
        opacity: visible ? 1 : 0,
        transform: visible
          ? undefined
          : variant === "scale"
          ? "scale(0.94)"
          : `translate3d(0, ${distance}px, 0)`,
        transition: `opacity 0.7s ${EASE} ${delay}ms, transform 0.7s ${EASE} ${delay}ms`,
        willChange: "opacity, transform",
      };

  return { ref, style, inView: visible };
}

/**
 * A "drawn line" reveal for hairline dividers: the rule scales from 0 to full
 * along its length when it enters view. Origin is the top for vertical rules.
 */
export function useDraw({ delay = 0, threshold = 0.2 }: { delay?: number; threshold?: number } = {}) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold,
    rootMargin: "0px 0px -8% 0px",
  });

  const visible = REDUCED || inView;

  const style: CSSProperties | undefined = REDUCED
    ? undefined
    : {
        transform: visible ? "scaleY(1)" : "scaleY(0)",
        transformOrigin: "top",
        transition: `transform 0.9s ${EASE} ${delay}ms`,
      };

  return { ref, style };
}
